import { useEffect, useMemo, useRef } from 'react';

import {
  findCorrectableTermRange,
  getCorrectableTermRanges,
  type TranscriptWordSelection,
} from './meetings-transcript-display.utils';

const HIGHLIGHT_NAME = 'transcript-correction-word';
const LONG_PRESS_MS = 450;
const LONG_PRESS_SLOP_PX = 10;

export function CorrectableTranscriptText({
  text,
  onSelectWord,
}: {
  text: string;
  onSelectWord: (selection: TranscriptWordSelection) => void;
}) {
  const elementRef = useRef<HTMLSpanElement>(null);
  const wordIndex = useRef(0);
  const selectionBeforeContextMenu = useRef<boolean | null>(null);
  const activeHighlight = useRef<Highlight | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const longPressOrigin = useRef<{ x: number; y: number } | null>(null);
  const ranges = useMemo(() => getCorrectableTermRanges(text), [text]);

  const clearHighlight = () => {
    if (
      typeof CSS !== 'undefined' &&
      CSS.highlights?.get(HIGHLIGHT_NAME) === activeHighlight.current
    ) {
      CSS.highlights.delete(HIGHLIGHT_NAME);
    }
    activeHighlight.current = null;
  };

  useEffect(() => {
    wordIndex.current = 0;
    const highlightRef = activeHighlight;
    return () => {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = undefined;
      if (
        typeof CSS !== 'undefined' &&
        CSS.highlights?.get(HIGHLIGHT_NAME) === highlightRef.current
      ) {
        CSS.highlights.delete(HIGHLIGHT_NAME);
      }
      highlightRef.current = null;
    };
  }, [text]);

  const rangeFor = (index: number) => {
    const node = elementRef.current?.firstChild;
    const termRange = ranges[index];
    if (!node || node.nodeType !== Node.TEXT_NODE || !termRange) return null;
    const range = document.createRange();
    range.setStart(node, termRange.start);
    range.setEnd(node, termRange.end);
    return range;
  };

  const highlight = (index: number) => {
    wordIndex.current = index;
    const range = rangeFor(index);
    if (
      !range ||
      typeof CSS === 'undefined' ||
      !CSS.highlights ||
      typeof Highlight === 'undefined'
    ) {
      return;
    }
    activeHighlight.current = new Highlight(range);
    CSS.highlights.set(HIGHLIGHT_NAME, activeHighlight.current);
  };

  const selectWord = (index: number) => {
    const range = rangeFor(index);
    if (!range) return;
    onSelectWord({
      term: ranges[index].term,
      rect: range.getBoundingClientRect(),
      focusTarget: elementRef.current ?? undefined,
    });
  };

  const cancelLongPress = () => {
    clearTimeout(longPressTimer.current);
    longPressTimer.current = undefined;
  };

  // Touch has no right-click: holding a word plays the context-menu role
  const tryCorrectWordAtPoint = (x: number, y: number) => {
    if (window.getSelection()?.isCollapsed === false) return;
    const index = wordAtPoint(x, y);
    if (index < 0) return;
    highlight(index);
    selectWord(index);
  };

  const wordAtPoint = (x: number, y: number) => {
    const caretDocument = document as Document & {
      caretPositionFromPoint?: (
        x: number,
        y: number
      ) => { offsetNode: Node; offset: number } | null;
      caretRangeFromPoint?: (x: number, y: number) => Range | null;
    };
    const position = caretDocument.caretPositionFromPoint?.(x, y);
    const caret = position ? null : caretDocument.caretRangeFromPoint?.(x, y);
    const node = position?.offsetNode ?? caret?.startContainer;
    const offset = position?.offset ?? caret?.startOffset;
    if (node !== elementRef.current?.firstChild || offset === undefined) {
      return -1;
    }
    return findCorrectableTermRange(ranges, offset);
  };

  if (!ranges.length) return <>{text}</>;

  return (
    <span
      ref={elementRef}
      tabIndex={-1}
      role="group"
      aria-label="Transcript words"
      aria-description="Right-click or touch and hold a word to correct it. Use Left/Right to choose, Enter to correct, and Escape to return to the row."
      aria-keyshortcuts="Shift+F10"
      data-transcript-correctable
      className="rounded-sm cursor-text outline-none"
      onPointerDown={(event) => {
        if (event.button === 2 || (event.button === 0 && event.ctrlKey)) {
          // macOS can select a word as part of the context-menu gesture
          selectionBeforeContextMenu.current =
            window.getSelection()?.isCollapsed === false;
          return;
        }
        if (event.pointerType === 'mouse' || event.button !== 0) return;
        cancelLongPress();
        const { clientX, clientY } = event;
        longPressOrigin.current = { x: clientX, y: clientY };
        longPressTimer.current = setTimeout(() => {
          longPressTimer.current = undefined;
          tryCorrectWordAtPoint(clientX, clientY);
        }, LONG_PRESS_MS);
      }}
      onContextMenu={(event) => {
        // Android and touch Windows fire contextmenu on a hold. The hold's
        // timer is cancelled below; if it already fired, this reselects the
        // same word
        cancelLongPress();
        const hadSelection =
          selectionBeforeContextMenu.current ??
          window.getSelection()?.isCollapsed === false;
        selectionBeforeContextMenu.current = null;
        if (hadSelection) return;

        const index = wordAtPoint(event.clientX, event.clientY);
        if (index < 0) return;
        event.preventDefault();
        event.stopPropagation();
        highlight(index);
        selectWord(index);
      }}
      onPointerMove={(event) => {
        const origin = longPressOrigin.current;
        if (
          longPressTimer.current !== undefined &&
          origin &&
          Math.hypot(event.clientX - origin.x, event.clientY - origin.y) >
            LONG_PRESS_SLOP_PX
        ) {
          cancelLongPress();
        }
        if (event.buttons || window.getSelection()?.isCollapsed === false) {
          clearHighlight();
          return;
        }
        const index = wordAtPoint(event.clientX, event.clientY);
        if (index < 0) clearHighlight();
        else highlight(index);
      }}
      onPointerUp={cancelLongPress}
      onPointerCancel={cancelLongPress}
      onPointerLeave={() => {
        cancelLongPress();
        clearHighlight();
      }}
      onFocus={() => highlight(wordIndex.current)}
      onBlur={clearHighlight}
      onKeyDown={(event) => {
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        if (event.key === 'Escape') {
          const row =
            event.currentTarget.closest<HTMLElement>('[role="option"]');
          if (row) {
            event.preventDefault();
            event.stopPropagation();
            row.focus();
          }
          return;
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          event.stopPropagation();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          highlight(
            Math.max(
              0,
              Math.min(ranges.length - 1, wordIndex.current + direction)
            )
          );
          return;
        }

        const isContextKey =
          event.key === 'ContextMenu' ||
          (event.key === 'F10' && event.shiftKey);
        if (!isContextKey && event.key !== 'Enter' && event.key !== ' ') return;
        if (window.getSelection()?.isCollapsed === false) return;
        event.preventDefault();
        event.stopPropagation();
        selectWord(Math.min(wordIndex.current, ranges.length - 1));
      }}
    >
      {text}
    </span>
  );
}
